/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, TrendingUp, GitBranch, Compass } from 'lucide-react';

/**
 * Phase 5.4 — Territory Evolution Score™ page.
 *
 * Calm composite of eight stewardship dimensions per centre, with a
 * Trajectory Whisper™ (deterministic phrase) and 3-point trend.
 * Sibling list shows centre comparison — never ranked.
 */
export default function TerritoryEvolutionPage() {
  const [comparison, setComparison] = useState(null);
  const [centreId, setCentreId] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/terra-growth/evolution-score/comparison?window=${windowDays}`)
      .then(r => {
        setComparison(r.data);
        if (!centreId && r.data.centres?.length > 0) {
          setCentreId(r.data.centres[0].centre_id);
        }
      })
      .catch(() => setComparison(null))
      .finally(() => setLoading(false));
  }, [windowDays]);

  useEffect(() => {
    if (!centreId) return;
    axios.get(`${API}/terra-growth/evolution-score?centre_id=${centreId}&window=${windowDays}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail(null));
  }, [centreId, windowDays]);

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="territory-evolution-page" className="terra-page-pad max-w-7xl mx-auto px-8 py-12">
        <Header windowDays={windowDays} setWindowDays={setWindowDays} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <aside className="lg:col-span-1">
            <ComparisonList comparison={comparison} loading={loading}
                            centreId={centreId} setCentreId={setCentreId} />
          </aside>
          <section className="lg:col-span-2">
            {detail ? (
              <Detail data={detail} />
            ) : (
              <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
                {loading ? <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                         : 'Select a centre to read its evolution score.'}
              </div>
            )}
          </section>
        </div>
        <p className="text-[11px] text-[#8A8681] italic max-w-2xl mt-12 pt-6 border-t border-[#F4F2EE]">
          {detail?.philosophy_note ||
           "Territory Evolution Score™ — eight calm stewardship dimensions, " +
           "deterministically composed."}
        </p>
      </div>
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Header
// ═══════════════════════════════════════════════════════════════════
function Header({ windowDays, setWindowDays }) {
  return (
    <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
      <div className="space-y-3 max-w-2xl">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C6239] flex items-center gap-2">
          <Compass className="w-3 h-3" /> Terra Growth OS™ · Visualization
        </p>
        <h1 className="text-4xl font-['Outfit'] font-light text-[#2C2A29] tracking-tight">
          Territory Evolution Score
        </h1>
        <p className="text-sm text-[#8A8681] font-['Work_Sans'] leading-relaxed">
          A deterministic composite of eight stewardship dimensions. The
          Trajectory Whisper™ reads the score deltas as a calm sentence —
          never a forecast, never motivational copy.
        </p>
      </div>
      <div className="flex items-center gap-2">
        {[30, 60, 90].map(d => (
          <button key={d} data-testid={`window-${d}`}
                  onClick={() => setWindowDays(d)}
                  className={`text-[11px] rounded-full px-3.5 py-1.5 border transition ${
                    windowDays === d ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]'
                                  : 'bg-white text-[#5C5955] border-[#D8D4CE] hover:border-[#B5B0A8]'}`}>
            last {d}d
          </button>
        ))}
      </div>
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Comparison list (sidebar)
// ═══════════════════════════════════════════════════════════════════
const BAND_TONE = {
  maturing:            'border-emerald-200 bg-emerald-50/50 text-emerald-800',
  stewarded:           'border-sky-200 bg-sky-50/50 text-sky-800',
  stabilizing:         'border-stone-200 bg-stone-50/50 text-stone-700',
  softening:           'border-amber-200 bg-amber-50/50 text-amber-800',
  calibration_needed:  'border-amber-300 bg-amber-50 text-amber-900',
  quiet_growth:        'border-stone-200 bg-stone-50/30 text-stone-600',
};

function ComparisonList({ comparison, loading, centreId, setCentreId }) {
  return (
    <article data-testid="comparison-list"
             className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
        Centres
      </p>
      {loading ? (
        <div className="text-[11px] text-[#8A8681]"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Composing…</div>
      ) : !comparison?.centres?.length ? (
        <p data-testid="comparison-empty"
           className="text-[11px] text-[#B5B0A8] italic">
          No centres in scope yet.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {comparison.centres.map(c => (
            <li key={c.centre_id}>
              <button data-testid={`centre-row-${c.centre_id}`}
                      onClick={() => setCentreId(c.centre_id)}
                      className={`w-full text-left rounded-xl border px-3 py-2.5 transition ${
                        centreId === c.centre_id
                          ? 'border-[#4A5D4E] bg-[#FBFaf9]'
                          : 'border-transparent hover:bg-[#FBFaf9]'
                      }`}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[12px] text-[#2C2A29] truncate">
                    {c.centre_id}
                  </span>
                  <span className="text-[14px] font-['Outfit'] tabular-nums text-[#2C2A29]">
                    {c.composite}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[9px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${BAND_TONE[c.band] || BAND_TONE.quiet_growth}`}>
                    {c.band.replace('_', ' ')}
                  </span>
                  <span className="text-[10px] tabular-nums text-[#8A8681]">
                    Δ {c.delta > 0 ? '+' : ''}{c.delta}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {comparison?.framing_note && (
        <p className="text-[10px] text-[#8A8681] italic mt-4 leading-relaxed">
          {comparison.framing_note}
        </p>
      )}
    </article>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Detail panel
// ═══════════════════════════════════════════════════════════════════
function Detail({ data }) {
  const cur = data.current || {};
  return (
    <div data-testid="evolution-detail" className="space-y-6">
      <ScoreCard data={data} />
      <Whisper text={data.trajectory_whisper} />
      <TrendStrip trend={data.trend} />
      <Contributors items={data.top_contributors} />
    </div>
  );
}

function ScoreCard({ data }) {
  const cur = data.current || {};
  const tone = BAND_TONE[cur.band] || BAND_TONE.quiet_growth;
  return (
    <article data-testid="score-card"
             className="rounded-2xl border border-[#EBE8E3] bg-white p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681]">
          {data.centre_id} · last {data.window_days} days
        </p>
        <span data-testid="score-band"
              className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${tone}`}>
          {(cur.band || 'quiet_growth').replace('_', ' ')}
        </span>
      </div>
      <div className="flex items-baseline gap-4">
        <span data-testid="score-composite"
              className="text-6xl font-['Outfit'] font-light tabular-nums text-[#2C2A29]">
          {cur.composite ?? 0}
        </span>
        <span className={`text-[14px] tabular-nums ${
          (data.delta || 0) >= 0 ? 'text-emerald-700' : 'text-stone-500'}`}>
          Δ {data.delta > 0 ? '+' : ''}{data.delta || 0} vs prior window
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 text-[11px] text-[#8A8681]">
        <span>confidence · {cur.confidence}</span>
        <span>·</span>
        <span>sample · <span className="tabular-nums">{cur.sample}</span></span>
        <span>·</span>
        <span>leads · <span className="tabular-nums">{cur.lead_total}</span></span>
      </div>
    </article>
  );
}

function Whisper({ text }) {
  if (!text) return null;
  return (
    <article data-testid="trajectory-whisper"
             className="rounded-2xl border border-[#E5D6BE] bg-[#FAF1E1]/40 p-5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C6239] mb-2 flex items-center gap-1">
        <GitBranch className="w-3 h-3" /> Trajectory Whisper™
      </p>
      <p className="text-base text-[#5C3D1F] leading-relaxed font-['Outfit'] font-light italic">
        {text}
      </p>
    </article>
  );
}

function TrendStrip({ trend }) {
  if (!trend?.length) return null;
  const max = Math.max(1, ...trend.map(t => t.composite || 0));
  return (
    <article data-testid="trend-strip"
             className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-4 flex items-center gap-1">
        <TrendingUp className="w-3 h-3" /> 3-window trend
      </p>
      <div className="flex items-end gap-3">
        {trend.map((t, i) => {
          const h = Math.max(8, (t.composite / max) * 80);
          const tone = BAND_TONE[t.band] || BAND_TONE.quiet_growth;
          return (
            <div key={i} data-testid={`trend-bar-${i}`}
                 className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full rounded-t-md bg-[#B5C2A8]"
                   style={{ height: `${h}px` }} />
              <span className="text-[14px] font-['Outfit'] tabular-nums text-[#2C2A29]">
                {t.composite}
              </span>
              <span className={`text-[9px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${tone}`}>
                {(t.band || 'quiet_growth').replace('_', ' ')}
              </span>
              <span className="text-[10px] text-[#8A8681] text-center">
                {t.window_label}
              </span>
            </div>
          );
        })}
      </div>
    </article>
  );
}

function Contributors({ items }) {
  if (!items?.length) return null;
  return (
    <article data-testid="contributors"
             className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] mb-3">
        Score contributors
      </p>
      <ul className="space-y-2">
        {items.map(c => (
          <li key={c.dimension}
              data-testid={`contrib-${c.dimension}`}
              className="flex items-center gap-3 text-[12px]">
            <span className="text-[#5C5955] flex-1 truncate">
              {prettyDim(c.dimension)}
            </span>
            <div className="flex-1 max-w-[240px] h-1.5 bg-[#F4F2EE] rounded-full overflow-hidden">
              <div className="h-full bg-[#4A5D4E]"
                   style={{ width: `${Math.min(100, c.score)}%` }} />
            </div>
            <span className="tabular-nums text-[#2C2A29] w-10 text-right">
              {c.score}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function prettyDim(k) {
  return k.replace(/_/g, ' ');
}
